// Message composer — a large floating card (positioned as an overlay by
// ChatView). Autosizing textarea, file attachments, send button. Uploads
// pending files on send, then dispatches the user_message.

import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { DEFAULT_AGENT_ID, useChatStore } from '../../store/chatStore';
import type { Attachment } from '../../protocol/types';
import { MAX_UPLOAD_SIZE, NiuUploadError } from '../../transport';
import { FILES_DROPPED_EVENT, type FilesDroppedDetail } from '../ui/DragDropOverlay';
import { CloseIcon, PaperclipIcon, SendIcon } from '../ui/Icons';
import { Spinner } from '../ui/Spinner';

interface PendingAttachment {
  localId: string;
  filename: string;
  size: number;
  mimeType: string;
  file: File;
  attachment?: Attachment;
  progress?: number;
  error?: string;
}

function uploadErrorLabel(err: unknown): string {
  if (err instanceof NiuUploadError) {
    if (err.detail.kind === 'fileTooLarge') return 'File too large';
    if (err.detail.kind === 'unauthorized') return 'Unauthorized';
    if (err.detail.kind === 'serverError') return `Server error ${err.detail.status}`;
  }
  return 'Upload failed';
}

export function Composer() {
  const send = useChatStore((s) => s.sendUserMessage);
  const upload = useChatStore((s) => s.uploadAttachment);
  const connection = useChatStore((s) => s.connection);
  const agentName = useChatStore((s) => {
    const active = s.agents.find((a) => a.id === s.activeAgentId);
    return active?.name ?? (s.activeAgentId === DEFAULT_AGENT_ID ? 'Niu' : 'Messages');
  });

  const [text, setText] = useState('');
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const disabled = connection.kind !== 'connected';
  const canSend = !disabled && !sending && (text.trim().length > 0 || pending.some((p) => !p.error));

  function autosize() {
    const ta = textareaRef.current;
    if (!ta || 'fieldSizing' in ta.style) return;
    ta.style.height = '1px';
    ta.style.height = `${Math.min(ta.scrollHeight, 220)}px`;
  }

  async function handleSend() {
    if (!canSend) return;
    setSending(true);

    // Upload every not-yet-uploaded file, collecting the resulting Attachment
    // refs into a local array. Reading them back via a setPending updater
    // would race the async render — the updater runs after `send` is called,
    // so the message would ship with no attachments.
    const ready: Attachment[] = [];
    let uploadFailed = false;
    for (const p of pending) {
      if (p.error) continue;
      if (p.attachment) {
        ready.push(p.attachment);
        continue;
      }
      try {
        const att = await upload(p.file, (frac) => {
          setPending((prev) =>
            prev.map((it) => (it.localId === p.localId ? { ...it, progress: frac } : it)),
          );
        });
        setPending((prev) =>
          prev.map((it) => (it.localId === p.localId ? { ...it, progress: 1, attachment: att } : it)),
        );
        ready.push(att);
      } catch (err) {
        setPending((prev) =>
          prev.map((it) => (it.localId === p.localId ? { ...it, error: uploadErrorLabel(err) } : it)),
        );
        uploadFailed = true;
      }
    }

    if (uploadFailed) {
      setSending(false);
      return;
    }

    const body = text;
    setPending([]);
    setText('');
    try {
      await send(body, ready);
    } finally {
      setSending(false);
      setTimeout(autosize, 0);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      void handleSend();
    }
  }

  function addFiles(files: File[]) {
    if (files.length === 0) return;
    const next: PendingAttachment[] = files.map((file) => {
      const localId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const base = { localId, filename: file.name, size: file.size, mimeType: file.type, file };
      return file.size > MAX_UPLOAD_SIZE ? { ...base, error: 'File exceeds 50 MB limit' } : base;
    });
    setPending((prev) => [...prev, ...next]);
  }

  function onFiles(e: ChangeEvent<HTMLInputElement>) {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    addFiles(files);
  }

  // Files dropped anywhere on the page (see DragDropOverlay) land here, the
  // same path as the in-bar picker.
  useEffect(() => {
    const onDropped = (e: Event) => {
      const detail = (e as CustomEvent<FilesDroppedDetail>).detail;
      if (detail?.files?.length) addFiles(detail.files);
    };
    window.addEventListener(FILES_DROPPED_EVENT, onDropped);
    return () => window.removeEventListener(FILES_DROPPED_EVENT, onDropped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {pending.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-2">
          {pending.map((p) => (
            <div
              key={p.localId}
              className={`flex items-center gap-2 rounded-lg px-2 py-1 text-xs ${
                p.error ? 'bg-error-bg text-error-text' : 'bg-elevated text-text-primary'
              }`}
            >
              {sending && !p.attachment && !p.error ? <Spinner size={12} /> : null}
              <span className="max-w-[12rem] truncate">{p.filename}</span>
              {sending && !p.error && !p.attachment && p.progress !== undefined ? (
                <span className="opacity-70">{Math.round(p.progress * 100)}%</span>
              ) : null}
              {p.error ? <span>{p.error}</span> : null}
              <button
                type="button"
                onClick={() => setPending((prev) => prev.filter((it) => it.localId !== p.localId))}
                className="opacity-60 hover:opacity-100"
                aria-label="Remove attachment"
              >
                <CloseIcon size={12} />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="flex flex-col gap-1.5 rounded-2xl bg-surface px-3 py-2.5 shadow-xl">
        <input ref={fileInputRef} type="file" multiple onChange={onFiles} className="hidden" />
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            autosize();
          }}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder={disabled ? 'Connecting…' : `Schreibe ${agentName}…`}
          disabled={disabled}
          className="scrollbar-thin max-h-[220px] min-h-[2.5rem] w-full resize-none overflow-x-hidden bg-transparent px-1 py-1.5 text-base leading-relaxed text-text-primary placeholder:text-text-faint focus:outline-none [field-sizing:content]"
        />
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            aria-label="Attach file"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-divider/50 hover:text-text-primary disabled:opacity-40"
          >
            <PaperclipIcon size={19} />
          </button>
          <button
            type="button"
            onClick={() => void handleSend()}
            disabled={!canSend}
            aria-label="Send"
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors ${
              canSend ? 'bg-accent text-white hover:opacity-90' : 'bg-divider text-text-faint'
            }`}
          >
            {sending ? <Spinner size={16} /> : <SendIcon size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
}
